'use client';

import { SModalContainer } from '../SModalContainer';
import { SCircleProgress } from '../SCircleProgress';
import { SIcon } from '../SIcon';
import { SButton } from '../SButton';

export type LoadingModalState = 'loading' | 'error';

const DEFAULT_MESSAGE: Record<LoadingModalState, string> = {
  loading: '작업 진행중...',
  error: '작업에 실패하였습니다. 다시 시도해 주세요.',
};
const DEFAULT_BUTTON_LABEL: Record<LoadingModalState, string> = {
  loading: '확인',
  error: '다시 시도',
};

export interface SLoadingModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** 닫기(X) 버튼 클릭 (sdClose) — error 상태에서만 노출 */
  onClose?: () => void;
  /** 백드롭·ESC로 안 닫히고 흔들림 (sd-modal-container persistent). **기본값 true** */
  persistent?: boolean;
  /** 상태 (loading: 스피너, error: 경고 아이콘) */
  state?: LoadingModalState;
  /** 진행률 (0–100) — 지정 시 determinate 스피너 */
  progress?: number;
  /** 메시지 (없으면 state 기본값) */
  message?: string | string[];
  /** 하단 버튼 표시 */
  useButton?: boolean;
  /** 버튼 레이블 (없으면 state 기본값) */
  buttonLabel?: string;
  /** 버튼 클릭 (sdClick) */
  onButtonClick?: () => void;
  width?: number | string;
  height?: number | string;
}

/** SLoadingModal — sd-loading-modal 포팅. state(loading/error) + progress + 옵션 버튼. */
export function SLoadingModal({
  open,
  onOpenChange,
  onClose,
  persistent,
  state = 'loading',
  progress,
  message,
  useButton = false,
  buttonLabel,
  onButtonClick,
  width = 520,
  height = 320,
}: SLoadingModalProps) {
  const resolvedMessage = message ?? DEFAULT_MESSAGE[state];
  const lines = Array.isArray(resolvedMessage)
    ? resolvedMessage
    : resolvedMessage
      ? [resolvedMessage]
      : [];
  // error 상태에서는 버튼이 필수로 표시됩니다.
  const showButton = useButton || state === 'error';

  return (
    <SModalContainer
      open={open}
      onOpenChange={onOpenChange}
      onClose={onClose}
      showClose={state === 'error'}
      persistent={persistent}
      ariaTitle={lines[0] || '로딩 중'}
      width={width}
      height={height}
    >
      {/* 카드가 화면 높이에 걸려 height(320)보다 작아지면 본문이 스크롤된다. justify-center 대신
          m-auto로 중앙정렬해야 넘칠 때 위쪽이 잘려 스크롤로도 못 보는 문제가 생기지 않는다 */}
      <div className="flex min-h-0 flex-auto flex-col overflow-y-auto px-[32px]">
        <div className="m-auto flex flex-col items-center gap-[20px]">
          {state === 'loading' ? (
            progress !== undefined ? (
              <SCircleProgress value={progress} />
            ) : (
              <SCircleProgress indeterminate />
            )
          ) : (
            <SIcon name="warningOutline" size={80} color="var(--cmp-modal-confirm-negative-icon)" />
          )}

          {lines.length > 0 && (
            <div className="flex flex-col gap-[2px]">
              {lines.map((m, i) => (
                <p
                  key={i}
                  className="m-0 max-w-[400px] break-keep text-center text-[12px] font-normal leading-[20px] text-[color:var(--cmp-modal-loading-message-color)]"
                >
                  {m}
                </p>
              ))}
            </div>
          )}

          {showButton && (
            <SButton
              color="neutral"
              outline
              size="sm"
              onClick={() => {
                onButtonClick?.();
                onOpenChange?.(false);
              }}
              label={buttonLabel ?? DEFAULT_BUTTON_LABEL[state]}
            />
          )}
        </div>
      </div>
    </SModalContainer>
  );
}
